import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, Tag, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/common/SEO";
import { Breadcrumbs } from "@/components/common/Breadcrumbs";
import { useCart } from "@/context/CartContext";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/format";

function variantLabel(product, variantId) {
  const v = product.variants?.find((x) => x.id === variantId);
  if (!v || v.title === "Default" || v.title === "Default Title") return null;
  return v.title;
}

export default function CartPage() {
  const { items, subtotal, updateQuantity, removeItem, clear, lineKey } = useCart();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [order, setOrder] = useState(null);

  const discountAmount = applied ? Math.round((subtotal * applied.value) / 100) : 0;
  const total = Math.max(0, subtotal - discountAmount);

  const applyCode = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.post("/discount/validate", { code });
      if (data.valid) {
        setApplied(data);
        toast.success(`Code ${data.code} applied — ${data.value}% off`);
      } else {
        setApplied(null);
        toast.error(data.message || "Invalid code");
      }
    } catch {
      toast.error("Could not validate code");
    }
  };

  const checkout = async () => {
    setPlacing(true);
    try {
      const payload = {
        items: items.map((it) => ({ product_id: it.product.handle, variant_id: it.variantId, quantity: it.quantity })),
        discount_code: applied?.code,
      };
      const { data } = await api.post("/checkout", payload);
      if (data.mode === "shopify" && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setOrder(data.order_number);
      clear();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
    } finally {
      setPlacing(false);
    }
  };

  if (order) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <SEO title="Order Confirmed" />
        <CheckCircle2 className="mx-auto h-14 w-14 text-gold" strokeWidth={1.3} />
        <h1 className="mt-6 font-serif text-4xl font-light text-ink">Thank you for your order</h1>
        <p className="mt-3 text-muted-foreground">
          Your order <span className="font-semibold text-ink">{order}</span> has been placed. A confirmation email is on its way.
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          (Demo checkout — connect a Shopify store in the backend to enable live payments.)
        </p>
        <Link to="/shop" className="mt-8 inline-block bg-ink px-10 py-4 text-[11px] uppercase tracking-[0.2em] text-ivory hover:bg-gold">
          Continue Shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <SEO title="Shopping Bag" />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Shopping Bag" }]} />
      <h1 className="mt-6 font-serif text-4xl font-light text-ink sm:text-5xl">Shopping Bag</h1>

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <ShoppingBag className="h-12 w-12 text-gold" strokeWidth={1.2} />
          <p className="mt-5 font-serif text-2xl text-ink">Your bag is empty</p>
          <p className="mt-2 text-sm text-muted-foreground">Explore our collections and find something you love.</p>
          <Link to="/shop" className="mt-6 bg-ink px-10 py-4 text-[11px] uppercase tracking-[0.2em] text-ivory hover:bg-gold">
            Start Shopping
          </Link>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 gap-12 lg:grid-cols-3">
          {/* Items */}
          <div className="lg:col-span-2">
            <ul className="divide-y divide-border border-y border-border">
              {items.map((it) => {
                const key = lineKey(it.product.handle, it.variantId);
                const vLabel = variantLabel(it.product, it.variantId);
                return (
                  <li key={key} className="flex gap-5 py-6">
                    <Link to={`/product/${it.product.handle}`} className="h-32 w-24 shrink-0 overflow-hidden bg-cream">
                      <img src={it.product.images?.[0]} alt={it.product.title} className="h-full w-full object-cover" />
                    </Link>
                    <div className="flex flex-1 flex-col">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-luxe text-[10px] text-gold">{it.product.category}</p>
                          <Link to={`/product/${it.product.handle}`}>
                            <h3 className="mt-1 font-serif text-xl text-ink hover:text-gold">{it.product.title}</h3>
                          </Link>
                          {vLabel && <p className="mt-1 text-xs text-muted-foreground">{it.product.options?.name}: {vLabel}</p>}
                        </div>
                        <button onClick={() => removeItem(key)} aria-label="Remove" className="text-muted-foreground hover:text-destructive">
                          <Trash2 size={17} />
                        </button>
                      </div>
                      <div className="mt-auto flex items-center justify-between pt-4">
                        <div className="flex items-center border border-border">
                          <button onClick={() => updateQuantity(key, it.quantity - 1)} className="grid h-9 w-9 place-items-center hover:text-gold" aria-label="Decrease">
                            <Minus size={13} />
                          </button>
                          <span className="w-9 text-center text-sm">{it.quantity}</span>
                          <button onClick={() => updateQuantity(key, it.quantity + 1)} className="grid h-9 w-9 place-items-center hover:text-gold" aria-label="Increase">
                            <Plus size={13} />
                          </button>
                        </div>
                        <span className="font-semibold text-ink">{formatINR(it.product.price * it.quantity)}</span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            <Link to="/shop" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink hover:text-gold">
              <ArrowRight size={14} className="rotate-180" /> Continue Shopping
            </Link>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="border border-border bg-white/60 p-6 lg:sticky lg:top-28">
              <h2 className="font-serif text-2xl text-ink">Order Summary</h2>

              <div className="mt-5">
                <label className="text-luxe text-[10px] text-ink">Discount Code</label>
                <div className="mt-2 flex">
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Enter code"
                    data-testid="cart-discount-input"
                    className="flex-1 border border-border bg-transparent px-3 py-2.5 text-sm focus:border-gold focus:outline-none"
                  />
                  <button onClick={applyCode} className="bg-ink px-4 text-[11px] uppercase tracking-wider text-ivory hover:bg-gold">
                    Apply
                  </button>
                </div>
                <p className="mt-1.5 text-[11px] text-muted-foreground">Try SHINE15 or WELCOME20</p>
              </div>

              <dl className="mt-6 space-y-3 border-t border-border pt-6 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Subtotal</dt>
                  <dd className="text-ink">{formatINR(subtotal)}</dd>
                </div>
                {applied && (
                  <div className="flex justify-between text-gold">
                    <dt className="flex items-center gap-1.5"><Tag size={13} /> {applied.code} ({applied.value}%)</dt>
                    <dd>−{formatINR(discountAmount)}</dd>
                  </div>
                )}
                <div className="flex justify-between border-t border-border pt-4 text-base">
                  <dt className="font-serif text-xl text-ink">Total</dt>
                  <dd className="font-serif text-xl text-ink" data-testid="cart-total">{formatINR(total)}</dd>
                </div>
                <p className="text-xs text-muted-foreground" data-testid="cart-shipping-note">Shipping & taxes calculated at checkout.</p>
              </dl>

              <button
                onClick={checkout}
                disabled={placing}
                data-testid="cart-page-checkout-button"
                className="mt-6 w-full bg-ink py-4 text-[11px] font-medium uppercase tracking-[0.2em] text-ivory transition-colors hover:bg-gold disabled:opacity-60"
              >
                {placing ? "Processing…" : "Proceed to Checkout"}
              </button>
              <p className="mt-3 text-center text-[11px] text-muted-foreground">Secure checkout · Taxes calculated at payment</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
